import { KeySet } from '@tableau/data-structures';
import * as React from 'react';

export type SelectionProviderProps<TChildProps> = {

  // The selectionId identifies the context in which the selection in valid.
  // When it changes, the selection is cleared.
  // For example, if the selection is columns in a table, then the identifier should be unique to that table.
  readonly selectionId: string;

  readonly children: (props: TChildProps) => React.ReactNode;
};

export type SelectionProviderChildProps = {
  readonly selectedKeys: KeySet;
  readonly updateSelectionForKey: (key: string) => void;
  readonly doubleClickForKey?: (key: string) => void;
  readonly clearSelection?: () => void;
};

type SelectionProviderState = {
  readonly selectedKeys: KeySet;
};


export type MultiSelectionProviderChildProps = SelectionProviderChildProps & {
  readonly setSelectedKeys: (selectedKeys: KeySet) => void;
};

export type MultiSelectionProviderProps = SelectionProviderProps<MultiSelectionProviderChildProps>;

export class MultiSelectionProvider extends React.Component<MultiSelectionProviderProps, SelectionProviderState> {

  public constructor(props: MultiSelectionProviderProps) {
    super(props);
    this.state = { selectedKeys: {} };
  }

  public componentDidUpdate(prevProps: SingleSelectionProviderProps) {
    if (this.props.selectionId !== prevProps.selectionId) {
      this.setState({ selectedKeys: {} });
    }
  }

  public render(): React.ReactNode {
    return this.props.children({
      selectedKeys: this.state.selectedKeys,
      setSelectedKeys: this.setSelectedKeys,
      updateSelectionForKey: this.updateSelectionForKey,
      clearSelection: this.clearSelection
    });
  }

  private setSelectedKeys = (selectedKeys: KeySet) => {
    this.setState({ selectedKeys });
  }

  private updateSelectionForKey = (key: string) => {
    this.setState(prevState => {
      const newKeys = { ...prevState.selectedKeys };
      if (prevState.selectedKeys[key]) {
        delete newKeys[key];
      } else {
        newKeys[key] = true;
      }
      return { selectedKeys: newKeys };
    });
  }

  private clearSelection = () => {
    this.setState({ selectedKeys: {} });
  }

}


export type SingleSelectionProviderChildProps = SelectionProviderChildProps;

export type SingleSelectionProviderProps = SelectionProviderProps<SingleSelectionProviderChildProps>;

export class SingleSelectionProvider extends React.Component<SingleSelectionProviderProps, SelectionProviderState> {

  public constructor(props: SingleSelectionProviderProps) {
    super(props);
    this.state = { selectedKeys: {} };
  }

  public componentDidUpdate(prevProps: SingleSelectionProviderProps) {
    if (this.props.selectionId !== prevProps.selectionId) {
      this.setState({ selectedKeys: {} });
    }
  }

  public render(): React.ReactNode {
    return this.props.children({
      selectedKeys: this.state.selectedKeys,
      updateSelectionForKey: this.updateSelectionForKey,
      doubleClickForKey: this.onDoubleClickForKey,
      clearSelection: this.clearSelection
    });
  }

  private updateSelectionForKey = (key: string) => {
    this.setState(prevState => {
      const newKey: KeySet = prevState.selectedKeys[key] ? {} : { [key]: true };
      return { selectedKeys: newKey };
    });
  }

  private onDoubleClickForKey = (key: string) => {
    this.setState({ selectedKeys: { [key]: true } });
  }

  private clearSelection = () => {
    this.setState({ selectedKeys: {} });
  }

}
